import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FormEvent, lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ErrorNotice, Layout, LoadingScreen } from '../components/Layout';
import { api, ApiClientError } from '../lib/api';
import type { MeetingLabel, MeetingNode, MeetingTopicSummary } from '../lib/types';
import { sanitizeSummaryHtml, SummaryEditorModal } from '../components/SummaryEditorModal';
import { MeetingActionsPanel } from '../components/MeetingActionsPanel';
import { SearchableTextPanel } from '../components/SearchableTextPanel';

interface TreeResponse {
  topic: MeetingTopicSummary;
  meetings: MeetingNode[];
}

const MeetingFilesPanel = lazy(() => import('../components/MeetingFilesPanel')
  .then((module) => ({ default: module.MeetingFilesPanel })));

const statusLabel: Record<MeetingNode['status'], string> = {
  draft: 'ร่าง',
  planned: 'นัดแล้ว',
  completed: 'เสร็จแล้ว',
  cancelled: 'ยกเลิก',
};

function formatDate(value: string | null) {
  if (!value) return 'ยังไม่ระบุวัน';
  return new Intl.DateTimeFormat('th-TH', { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(value),
  );
}

function depthsOf(meetings: MeetingNode[]) {
  const byId = new Map(meetings.map((meeting) => [meeting.id, meeting]));
  const cache = new Map<string, number>();
  const depth = (meeting: MeetingNode, path = new Set<string>()): number => {
    if (cache.has(meeting.id)) return cache.get(meeting.id)!;
    if (!meeting.previousMeetingId || path.has(meeting.id)) return 0;
    const parent = byId.get(meeting.previousMeetingId);
    if (!parent) return 0;
    path.add(meeting.id);
    const result = Math.min(20, depth(parent, path) + 1);
    cache.set(meeting.id, result);
    return result;
  };
  return new Map(meetings.map((meeting) => [meeting.id, depth(meeting)]));
}

export function MeetingTopicPage() {
  const { id = '' } = useParams();
  const client = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [view, setView] = useState<'tree' | 'timeline'>('tree');
  const [showCreate, setShowCreate] = useState(false);
  const [title, setTitle] = useState('');
  const [heldAt, setHeldAt] = useState('');
  const [previousMeetingId, setPreviousMeetingId] = useState<string | null>(null);
  const [editingSummary, setEditingSummary] = useState(false);
  const [showMoveTopic, setShowMoveTopic] = useState(false);
  const [moveTopicId, setMoveTopicId] = useState('');
  const [newMoveTopicTitle, setNewMoveTopicTitle] = useState('');
  const [showDeleteMeeting, setShowDeleteMeeting] = useState(false);
  const [deleteTreeAction, setDeleteTreeAction] = useState<'reparent' | 'orphan'>('reparent');
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const [newLabelName, setNewLabelName] = useState('');
  const [newLabelColor, setNewLabelColor] = useState('#0f766e');
  const summaryRef = useRef<HTMLDivElement>(null);
  const decisionsRef = useRef<HTMLDivElement>(null);
  const notesRef = useRef<HTMLDivElement>(null);
  const tree = useQuery({
    queryKey: ['meeting-tree', id],
    queryFn: () =>
      api<{ data: TreeResponse }>(`/api/meeting-topics/${id}/tree`).then((result) => result.data),
    enabled: Boolean(id),
  });
  const topics = useQuery({
    queryKey: ['meeting-topics'],
    queryFn: () => api<{ data: MeetingTopicSummary[] }>('/api/meeting-topics?status=active&limit=100')
      .then((result) => result.data),
  });
  const labels = useQuery({ queryKey: ['meeting-labels'], queryFn: () => api<{ data: MeetingLabel[] }>('/api/meeting-labels').then((result) => result.data) });
  const confirmTopic = useMutation({
    mutationFn: ({ meetingId, topicId }: { meetingId: string; topicId: string }) =>
      api(`/api/meetings/${meetingId}/confirm-topic`, {
        method: 'POST',
        body: JSON.stringify({ topicId }),
      }),
    onSuccess: async () => {
      setParams({});
      await Promise.all([
        client.invalidateQueries({ queryKey: ['meeting-tree', id] }),
        client.invalidateQueries({ queryKey: ['meeting-topics'] }),
      ]);
    },
  });
  const selectedId = params.get('meeting');
  const searchTerm = params.get('find') ?? '';
  const selected = tree.data?.meetings.find((meeting) => meeting.id === selectedId) ?? null;
  useEffect(() => {
    if (!searchTerm || !selected) return;
    const needle = searchTerm.toLocaleLowerCase('th');
    const target = selected.summary?.toLocaleLowerCase('th').includes(needle)
      ? summaryRef.current
      : selected.decisions?.toLocaleLowerCase('th').includes(needle)
        ? decisionsRef.current
        : selected.notes?.toLocaleLowerCase('th').includes(needle)
          ? notesRef.current
          : null;
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [searchTerm, selected]);
  const selectMeeting = (meetingId: string) => {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set('meeting', meetingId);
      next.delete('find');
      return next;
    });
    setEditingSummary(false);
    setSelectedLabels(tree.data?.meetings.find((meeting) => meeting.id === meetingId)?.labels.map((label) => label.id) ?? []);
  };
  const updateSummary = useMutation({
    mutationFn: (input: { summary: string }) => api(`/api/meetings/${selectedId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
    onSuccess: async () => {
      setEditingSummary(false);
      await client.invalidateQueries({ queryKey: ['meeting-tree', id] });
    },
  });
  const updateLabels = useMutation({ mutationFn: () => api(`/api/meetings/${selectedId}`, { method: 'PATCH', body: JSON.stringify({ labelIds: selectedLabels }) }), onSuccess: () => client.invalidateQueries({ queryKey: ['meeting-tree', id] }) });
  const createAndAssignLabel = useMutation({
    mutationFn: async () => {
      const created = await api<{ data: MeetingLabel }>('/api/meeting-labels', {
        method: 'POST',
        body: JSON.stringify({ name: newLabelName.trim(), color: newLabelColor }),
      });
      const labelIds = [...new Set([...selectedLabels, created.data.id])];
      await api(`/api/meetings/${selectedId}`, {
        method: 'PATCH',
        body: JSON.stringify({ labelIds }),
      });
      return { labelIds };
    },
    onSuccess: async ({ labelIds }) => {
      setSelectedLabels(labelIds);
      setNewLabelName('');
      setNewLabelColor('#0f766e');
      await Promise.all([
        client.invalidateQueries({ queryKey: ['meeting-labels'] }),
        client.invalidateQueries({ queryKey: ['meeting-tree', id] }),
      ]);
    },
  });
  const moveTopic = useMutation({
    mutationFn: (targetTopicId?: string) => api(`/api/meetings/${selectedId}/move-topic`, { method: 'POST', body: JSON.stringify({ topicId: targetTopicId ?? moveTopicId }) }),
    onSuccess: async () => {
      setShowMoveTopic(false);
      setMoveTopicId('');
      setParams({});
      await Promise.all([client.invalidateQueries({ queryKey: ['meeting-tree', id] }), client.invalidateQueries({ queryKey: ['meeting-topics'] })]);
    },
  });
  const createMoveTopic = useMutation({
    mutationFn: () => api<{ data: { id: string } }>('/api/meeting-topics', { method: 'POST', body: JSON.stringify({ title: newMoveTopicTitle, color: '#0f766e' }) }),
    onSuccess: (result) => { setMoveTopicId(result.data.id); setNewMoveTopicTitle(''); moveTopic.mutate(result.data.id); },
  });
  const deleteMeeting = useMutation({
    mutationFn: () => api(`/api/meetings/${selectedId}`, { method: 'DELETE', body: JSON.stringify({ treeAction: deleteTreeAction, confirm: true }) }),
    onSuccess: async () => { setShowDeleteMeeting(false); setParams({}); await Promise.all([client.invalidateQueries({ queryKey: ['meeting-tree', id] }), client.invalidateQueries({ queryKey: ['meeting-topics'] })]); },
  });
  const depths = useMemo(() => depthsOf(tree.data?.meetings ?? []), [tree.data?.meetings]);
  const columns = useMemo(() => {
    const result = new Map<number, MeetingNode[]>();
    for (const meeting of tree.data?.meetings ?? []) {
      const depth = depths.get(meeting.id) ?? 0;
      result.set(depth, [...(result.get(depth) ?? []), meeting]);
    }
    return [...result.entries()].sort(([a], [b]) => a - b);
  }, [depths, tree.data?.meetings]);
  const createMeeting = useMutation({
    mutationFn: () =>
      api('/api/meetings', {
        method: 'POST',
        body: JSON.stringify({
          topicId: id,
          previousMeetingId,
          title,
          heldAt: heldAt ? new Date(heldAt).toISOString() : null,
          status: heldAt ? 'planned' : 'draft',
          labelIds: [],
        }),
      }),
    onSuccess: async () => {
      setShowCreate(false);
      setTitle('');
      setHeldAt('');
      setPreviousMeetingId(null);
      await client.invalidateQueries({ queryKey: ['meeting-tree', id] });
    },
  });
  const openCreate = (parentId: string | null) => {
    setPreviousMeetingId(parentId);
    setShowCreate(true);
  };
  const onCreate = (event: FormEvent) => {
    event.preventDefault();
    if (title.trim()) createMeeting.mutate();
  };
  if (tree.isLoading)
    return (
      <Layout title="เรื่องประชุม" wide>
        <LoadingScreen message="กำลังสร้างแผนที่เรื่องประชุม…" />
      </Layout>
    );
  if (!tree.data || tree.isError)
    return (
      <Layout title="เรื่องประชุม" wide>
        <ErrorNotice message="เปิดเรื่องประชุมไม่สำเร็จ" />
      </Layout>
    );
  const meetings = tree.data.meetings;
  return (
    <Layout title={tree.data.topic.title} wide>
      <div className="space-y-5">
        <div className="flex flex-col gap-4 rounded-3xl bg-white p-5 shadow-sm ring-1 ring-slate-200 md:flex-row md:items-center md:justify-between">
          <div>
            <Link to="/meetings" className="text-sm font-semibold text-teal-700">
              ← ศูนย์บัญชาการ
            </Link>
            <h2 className="mt-2 text-2xl font-bold">{tree.data.topic.title}</h2>
            <p className="mt-1 text-sm text-slate-500">
              {tree.data.topic.description || 'ติดตามความต่อเนื่องและแขนงของการประชุม'}
            </p>
          </div>
          {!tree.data.topic.systemKey && (
            <button type="button" className="btn-primary" onClick={() => openCreate(null)}>
              ＋ เพิ่มการประชุมครั้งแรก
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex rounded-xl bg-slate-200 p-1">
            <button
              type="button"
              className={`rounded-lg px-4 py-2 text-sm font-semibold ${view === 'tree' ? 'bg-white text-teal-800 shadow-sm' : 'text-slate-600'}`}
              onClick={() => setView('tree')}
            >
              Tree Map
            </button>
            <button
              type="button"
              className={`rounded-lg px-4 py-2 text-sm font-semibold ${view === 'timeline' ? 'bg-white text-teal-800 shadow-sm' : 'text-slate-600'}`}
              onClick={() => setView('timeline')}
            >
              Timeline
            </button>
          </div>
          <p className="text-sm text-slate-500">{meetings.length} ครั้ง · แตกแขนงได้จากทุกครั้ง</p>
        </div>

        {view === 'tree' ? (
          <section className="overflow-x-auto rounded-3xl border border-slate-200 bg-slate-100/70 p-4 md:p-6">
            {columns.length ? (
              <div className="flex min-w-max items-start gap-10">
                {columns.map(([depth, nodes]) => (
                  <div key={depth} className="w-72 space-y-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                      {depth === 0 ? 'จุดเริ่มต้น' : `ต่อเนื่องระดับ ${depth}`}
                    </p>
                    {nodes.map((meeting) => (
                      <article
                        key={meeting.id}
                        className={`relative rounded-2xl border bg-white p-4 shadow-sm transition ${selectedId === meeting.id ? 'border-teal-500 ring-2 ring-teal-100' : 'border-slate-200 hover:border-teal-300'}`}
                      >
                        {depth > 0 && (
                          <span
                            className="absolute -left-10 top-8 text-2xl text-slate-300"
                            aria-hidden
                          >
                            →
                          </span>
                        )}
                        <button
                          type="button"
                          className="block w-full text-left"
                          onClick={() => selectMeeting(meeting.id)}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="font-bold text-slate-900">{meeting.title}</h3>
                            <span className="rounded-full bg-teal-50 px-2 py-1 text-[11px] font-bold text-teal-700">
                              {statusLabel[meeting.status]}
                            </span>
                          </div>
                          <p className="mt-2 text-sm text-slate-500">
                            {formatDate(meeting.heldAt)}
                          </p>
                          {meeting.summary && (
                            <p className="mt-2 line-clamp-3 text-sm text-slate-600">
                              {meeting.summary}
                            </p>
                          )}
                          <div className="mt-3 flex flex-wrap gap-1.5">
                            {meeting.labels.map((label) => (
                              <span
                                key={label.id}
                                className="rounded-full px-2 py-1 text-[11px] font-semibold text-white"
                                style={{ backgroundColor: label.color }}
                              >
                                {label.name}
                              </span>
                            ))}
                          </div>
                          <p className="mt-3 text-xs text-slate-400">
                            งานเปิด {meeting.openActionCount} · ไฟล์ {meeting.fileCount}
                          </p>
                        </button>
                        {!tree.data.topic.systemKey && (
                          <button
                            type="button"
                            className="mt-3 w-full rounded-xl border border-dashed border-teal-300 py-2 text-sm font-semibold text-teal-700"
                            onClick={() => openCreate(meeting.id)}
                          >
                            ＋ แตกแขนงจากครั้งนี้
                          </button>
                        )}
                      </article>
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-12 text-center">
                <p className="font-semibold">ยังไม่มีการประชุมในเรื่องนี้</p>
                {!tree.data.topic.systemKey && (
                  <button className="btn-primary mt-4" onClick={() => openCreate(null)}>
                    เพิ่มการประชุมครั้งแรก
                  </button>
                )}
              </div>
            )}
          </section>
        ) : (
          <section className="card">
            <div className="relative ml-3 border-l-2 border-teal-200 pl-6">
              {[...meetings]
                .sort((a, b) => (b.heldAt ?? b.createdAt).localeCompare(a.heldAt ?? a.createdAt))
                .map((meeting) => (
                  <button
                    type="button"
                    key={meeting.id}
                    className="relative block w-full border-b border-slate-100 py-4 text-left last:border-0"
                    onClick={() => selectMeeting(meeting.id)}
                  >
                    <span className="absolute -left-[31px] top-6 h-3 w-3 rounded-full bg-teal-600 ring-4 ring-white" />
                    <p className="text-xs font-semibold text-teal-700">
                      {formatDate(meeting.heldAt)}
                    </p>
                    <p className="mt-1 font-bold">{meeting.title}</p>
                    <p className="mt-1 line-clamp-2 text-sm text-slate-500">
                      {meeting.summary || 'ยังไม่มีสรุป'}
                    </p>
                  </button>
                ))}
            </div>
          </section>
        )}

        {selected && (
          <section className="grid gap-4 md:grid-cols-[minmax(0,1fr)_18rem] lg:grid-cols-[minmax(0,1fr)_20rem]">
            <div className="card">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-teal-700">รายละเอียดการประชุม</p>
                  <h2 className="mt-1 text-xl font-bold">{selected.title}</h2>
                </div>
                <button type="button" className="px-3 text-slate-500" onClick={() => setParams({})}>
                  ✕
                </button>
              </div>
              <div className="mt-3 flex flex-wrap justify-end gap-4"><button type="button" className="text-sm font-semibold text-teal-700" onClick={() => setShowMoveTopic(true)}>เปลี่ยนเรื่องประชุม</button><button type="button" className="text-sm font-semibold text-red-600" onClick={() => setShowDeleteMeeting(true)}>ลบการประชุม</button></div>
              <div className="mt-4 rounded-xl bg-slate-50 p-3">
                <div className="text-sm font-semibold">Label การประชุม</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(labels.data ?? []).map((label) => <label key={label.id} className="flex items-center gap-1.5 rounded-full border bg-white px-2 py-1 text-xs"><input type="checkbox" checked={selectedLabels.includes(label.id)} onChange={() => setSelectedLabels((current) => current.includes(label.id) ? current.filter((item) => item !== label.id) : [...current, label.id])} /><i className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: label.color }} />{label.name}</label>)}
                </div>
                <button type="button" className="mt-2 text-xs font-semibold text-teal-700" onClick={() => updateLabels.mutate()} disabled={updateLabels.isPending}>{updateLabels.isPending ? 'กำลังบันทึก…' : 'บันทึก Label'}</button>
                <form className="mt-3 grid gap-2 border-t border-slate-200 pt-3 sm:grid-cols-[minmax(0,1fr)_auto_auto]" onSubmit={(event) => { event.preventDefault(); if (newLabelName.trim() && selectedId) createAndAssignLabel.mutate(); }}>
                  <input className="field min-w-0" value={newLabelName} onChange={(event) => setNewLabelName(event.target.value)} placeholder="ชื่อ Label ใหม่" maxLength={80} aria-label="ชื่อ Label ใหม่" />
                  <label className="flex min-h-11 items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 text-xs font-medium text-slate-600"><input type="color" className="h-7 w-8 cursor-pointer border-0 bg-transparent p-0" value={newLabelColor} onChange={(event) => setNewLabelColor(event.target.value)} aria-label="สี Label ใหม่" />สี</label>
                  <button className="btn-secondary shrink-0" type="submit" disabled={!newLabelName.trim() || createAndAssignLabel.isPending}>{createAndAssignLabel.isPending ? 'กำลังสร้าง…' : '＋ สร้างและเลือก'}</button>
                </form>
                {createAndAssignLabel.error && <div className="mt-2"><ErrorNotice message={createAndAssignLabel.error.message} /></div>}
              </div>
              <dl className="mt-4 grid gap-3 sm:grid-cols-2">
                <div>
                  <dt className="text-xs text-slate-500">วันเวลา</dt>
                  <dd className="font-medium">{formatDate(selected.heldAt)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-slate-500">สถานที่</dt>
                  <dd className="font-medium">{selected.location || '-'}</dd>
                </div>
              </dl>
              <div ref={summaryRef} className="mt-5 scroll-mt-24">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-sm font-bold">สรุป</h3>
                  {!editingSummary && (
                    <button type="button" className="text-sm font-semibold text-teal-700" onClick={() => {
                      setEditingSummary(true);
                    }}>
                      แก้ไขสรุป
                    </button>
                  )}
                </div>
                <div className="mt-2">
                  {selected.summary ? (
                    <SearchableTextPanel key={`${selected.id}-summary-${searchTerm}`} title="สรุปการประชุม" text={sanitizeSummaryHtml(selected.summary)} initialQuery={searchTerm} richHtml />
                  ) : <p className="text-sm text-slate-600">ยังไม่มีสรุป ยืนยันข้อมูลหลังอัปโหลด Transcript/Summary</p>}
                </div>
                {updateSummary.error && <ErrorNotice message={updateSummary.error.message} />}
              </div>
              {selected.sourceDocumentUrl && (
                <a className="btn-secondary mt-4 inline-flex" href={selected.sourceDocumentUrl} target="_blank" rel="noreferrer">
                  เปิดไฟล์เชิญประชุมต้นฉบับ
                </a>
              )}
              {selected.decisions && (
                <div ref={decisionsRef} className="mt-5 scroll-mt-24">
                  <h3 className="text-sm font-bold">มติ/ข้อสั่งการ</h3>
                  <div className="mt-2"><SearchableTextPanel key={`${selected.id}-decisions-${searchTerm}`} title="มติและข้อสั่งการ" text={selected.decisions} initialQuery={searchTerm} /></div>
                </div>
              )}
              {selected.notes && (
                <div ref={notesRef} className="mt-5 scroll-mt-24">
                  <h3 className="text-sm font-bold">บันทึก</h3>
                  <div className="mt-2"><SearchableTextPanel key={`${selected.id}-notes-${searchTerm}`} title="บันทึกการประชุม" text={selected.notes} initialQuery={searchTerm} /></div>
                </div>
              )}
              <Suspense fallback={<p className="mt-5 text-sm text-slate-500">กำลังเปิดส่วนจัดการไฟล์…</p>}>
                <MeetingFilesPanel key={selected.id} meetingId={selected.id} />
              </Suspense>
              <MeetingActionsPanel meetingId={selected.id} />
            </div>
            <aside className="card h-fit">
              <h3 className="font-bold">ข้อมูลประกอบ</h3>
              <div className="mt-3 space-y-2 text-sm">
                <p>
                  ไฟล์แนบ <strong className="float-right">{selected.fileCount}</strong>
                </p>
                <p>
                  งานที่ยังเปิด <strong className="float-right">{selected.openActionCount}</strong>
                </p>
              </div>
              {!tree.data.topic.systemKey && (
                <button
                  type="button"
                  className="btn-secondary mt-4 w-full"
                  onClick={() => openCreate(selected.id)}
                >
                  เพิ่มครั้งถัดไป
                </button>
              )}
              {tree.data.topic.systemKey === 'calendar_pending_classification' && (
                <div className="mt-5 border-t border-slate-200 pt-4">
                  <h3 className="font-bold">เลือกเรื่องประชุม</h3>
                  <p className="mt-1 text-xs text-slate-500">AI แนะนำเบื้องต้น คุณต้องยืนยันก่อนย้าย</p>
                  <div className="mt-3 space-y-2">
                    {(selected.topicSuggestions ?? []).map((suggestion) => (
                      <button
                        key={suggestion.topicId}
                        type="button"
                        className="w-full rounded-xl border border-slate-200 p-3 text-left hover:border-teal-400"
                        onClick={() => confirmTopic.mutate({ meetingId: selected.id, topicId: suggestion.topicId })}
                        disabled={confirmTopic.isPending}
                      >
                        <span className="font-semibold">{suggestion.title}</span>
                        <span className="block text-xs text-slate-500">{suggestion.reason}</span>
                      </button>
                    ))}
                  </div>
                  <label className="mt-3 block">
                    <span className="label">หรือเลือกเรื่องอื่น</span>
                    <select
                      className="field"
                      defaultValue=""
                      onChange={(event) => {
                        if (event.target.value && confirm('ยืนยันย้ายการประชุมไปยังเรื่องที่เลือก?')) {
                          confirmTopic.mutate({ meetingId: selected.id, topicId: event.target.value });
                        }
                      }}
                    >
                      <option value="">เลือกเรื่อง…</option>
                      {(topics.data ?? []).filter((topic) => !topic.systemKey).map((topic) => (
                        <option key={topic.id} value={topic.id}>{topic.title}</option>
                      ))}
                    </select>
                  </label>
                </div>
              )}
              {confirmTopic.error && <div className="mt-3"><ErrorNotice message={confirmTopic.error.message} /></div>}
            </aside>
          </section>
        )}
      </div>

        {editingSummary && selected && (
          <SummaryEditorModal
            meetingId={selected.id}
            initialValue={selected.summary ?? ''}
            saving={updateSummary.isPending}
            onCancel={() => setEditingSummary(false)}
            onSave={(value) => updateSummary.mutate({ summary: value })}
          />
        )}

        {showMoveTopic && selected && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4" role="dialog" aria-modal="true">
            <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
              <h2 className="text-lg font-bold">เปลี่ยนเรื่องประชุม</h2>
              <p className="mt-1 text-sm text-slate-500">ย้ายทั้งกิ่งและคงลิงก์ครั้งก่อนหน้าไว้</p>
              <select className="field mt-4" value={moveTopicId} onChange={(event) => setMoveTopicId(event.target.value)}>
                <option value="">เลือกเรื่องปลายทาง…</option>
                {(topics.data ?? []).filter((topic) => !topic.systemKey && topic.id !== selected.topicId).map((topic) => <option key={topic.id} value={topic.id}>{topic.title}</option>)}
              </select>
              <div className="mt-3 flex gap-2"><input className="field" value={newMoveTopicTitle} onChange={(event) => setNewMoveTopicTitle(event.target.value)} placeholder="หรือพิมพ์ชื่อเรื่องใหม่…" /><button type="button" className="btn-secondary whitespace-nowrap" disabled={!newMoveTopicTitle.trim() || createMoveTopic.isPending} onClick={() => createMoveTopic.mutate()}>สร้างใหม่</button></div>
              {moveTopic.error && <div className="mt-3"><ErrorNotice message={moveTopic.error instanceof Error ? moveTopic.error.message : 'ย้ายเรื่องไม่สำเร็จ'} /></div>}
              <div className="mt-5 grid grid-cols-2 gap-2"><button type="button" className="btn-secondary" onClick={() => setShowMoveTopic(false)}>ยกเลิก</button><button type="button" className="btn-primary" disabled={!moveTopicId || moveTopic.isPending || createMoveTopic.isPending} onClick={() => moveTopic.mutate()}>{moveTopic.isPending ? 'กำลังย้าย…' : 'ยืนยันย้ายทั้งกิ่ง'}</button></div>
            </div>
          </div>
        )}

        {showDeleteMeeting && selected && <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4" role="dialog" aria-modal="true"><div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl"><h2 className="text-lg font-bold text-red-700">ยืนยันการลบการประชุม</h2><p className="mt-2 text-sm text-slate-600">รายการนี้จะถูกซ่อนจากระบบ และไม่ลบไฟล์ต้นฉบับหรือ Calendar Event</p><label className="mt-4 block text-sm font-medium">จัดการรายการลูกใน Tree<select className="field mt-1" value={deleteTreeAction} onChange={(event) => setDeleteTreeAction(event.target.value as 'reparent' | 'orphan')}><option value="reparent">ย้ายไปต่อกับการประชุมก่อนหน้า</option><option value="orphan">ตัดลิงก์ ทำเป็นจุดเริ่มต้นใหม่</option></select></label>{deleteMeeting.error && <div className="mt-3"><ErrorNotice message={deleteMeeting.error instanceof Error ? deleteMeeting.error.message : 'ลบไม่สำเร็จ'} /></div>}<div className="mt-5 grid grid-cols-2 gap-2"><button className="btn-secondary" onClick={() => setShowDeleteMeeting(false)}>ยกเลิก</button><button className="btn-primary bg-red-600 hover:bg-red-700" onClick={() => deleteMeeting.mutate()} disabled={deleteMeeting.isPending}>{deleteMeeting.isPending ? 'กำลังลบ…' : 'ยืนยันลบ'}</button></div></div></div>}

        {showCreate && (
        <div
          className="fixed inset-0 z-50 flex items-end bg-slate-950/50 sm:items-center sm:justify-center sm:p-4"
          role="dialog"
          aria-modal="true"
        >
          <form
            onSubmit={onCreate}
            className="w-full rounded-t-3xl bg-white p-5 shadow-2xl sm:max-w-lg sm:rounded-3xl"
          >
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">เพิ่มการประชุม</h2>
                <p className="text-sm text-slate-500">
                  {previousMeetingId
                    ? 'สร้างเป็นแขนงต่อจากครั้งที่เลือก'
                    : 'สร้างเป็นจุดเริ่มต้นใหม่ในเรื่องนี้'}
                </p>
              </div>
              <button
                type="button"
                className="px-3 text-slate-500"
                onClick={() => setShowCreate(false)}
              >
                ✕
              </button>
            </div>
            <label className="mt-5 block">
              <span className="label">หัวข้อประชุม *</span>
              <input
                autoFocus
                className="field"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
              />
            </label>
            <label className="mt-3 block">
              <span className="label">วันและเวลา (ไม่บังคับ)</span>
              <input
                type="datetime-local"
                className="field"
                value={heldAt}
                onChange={(event) => setHeldAt(event.target.value)}
              />
            </label>
            <label className="mt-3 block">
              <span className="label">ครั้งก่อนหน้า</span>
              <select
                className="field"
                value={previousMeetingId ?? ''}
                onChange={(event) => setPreviousMeetingId(event.target.value || null)}
              >
                <option value="">ไม่มี — เป็นจุดเริ่มต้น</option>
                {meetings.map((meeting) => (
                  <option key={meeting.id} value={meeting.id}>
                    {meeting.title} · {formatDate(meeting.heldAt)}
                  </option>
                ))}
              </select>
            </label>
            {createMeeting.error && (
              <div className="mt-3">
                <ErrorNotice
                  message={
                    createMeeting.error instanceof ApiClientError
                      ? createMeeting.error.message
                      : 'เพิ่มการประชุมไม่สำเร็จ'
                  }
                />
              </div>
            )}
            <div className="mt-5 grid grid-cols-2 gap-3">
              <button type="button" className="btn-secondary" onClick={() => setShowCreate(false)}>
                ยกเลิก
              </button>
              <button className="btn-primary" disabled={!title.trim() || createMeeting.isPending}>
                {createMeeting.isPending ? 'กำลังบันทึก…' : 'บันทึก'}
              </button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
}
